import type { CSSProperties, FC } from 'react'
import { Loader, Progress } from '@mantine/core'
import {
    IconShoppingCartPlus,
    IconCheck,
    IconAlertTriangle,
    IconPencil,
} from '@tabler/icons-react'
import { useMutationState } from '@tanstack/react-query'
import { useAgent } from '@copilotkit/react-core/v2'
import type { AgentState } from '@/features/thread/types'
import {
    FONT,
    TEXT,
    DIMMED,
    BRAND,
    BORDER,
    SURFACE,
    ON_PRIMARY,
} from './widgetStyles'
import { formatReach, datasetLabel } from './renderMappers'
import { colorForDataset } from '@/lib/datasetColors'
import { DatasetType, type AudienceConfiguration } from '@/lib/domain'
import { cpmFor, formatCpm } from '@/lib/pricing'

// ---- styles -----------------------------------------------------------------
const summaryStyle: CSSProperties = {
    fontFamily: FONT,
    fontSize: 'var(--mantine-font-size-sm)',
    color: TEXT,
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
}
// Each strategy is its own bordered sub-card stacked inside the outer card.
const strategyRowStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    padding: 'var(--mantine-spacing-sm) var(--mantine-spacing-md)',
    border: `1px solid ${BORDER}`,
    borderRadius: 'var(--mantine-radius-md)',
    background: SURFACE,
}
const titleStyle: CSSProperties = {
    fontFamily: FONT,
    fontWeight: 500,
    fontSize: 'var(--mantine-font-size-sm)',
    lineHeight: 1.35,
    color: TEXT,
}
const descStyle: CSSProperties = {
    fontFamily: FONT,
    fontSize: 'var(--mantine-font-size-sm)',
    color: DIMMED,
    lineHeight: 1.45,
}
// Reach — a compact number + "REACH" label, matching the cart sidebar's
// treatment but at the chat's smaller scale. Sits at the top-right of the card,
// beside the title.
const reachValueStyle: CSSProperties = {
    fontFamily: FONT,
    fontSize: 'var(--mantine-font-size-sm)',
    fontWeight: 700,
    lineHeight: 1.1,
    color: BRAND,
}
const reachLabelStyle: CSSProperties = {
    fontFamily: FONT,
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    lineHeight: 1.2,
    color: DIMMED,
}
// Dataset pills are colour-coded per dataset via the shared `colorForDataset`
// mapping (so a dataset's colour matches the cart chips). The `*-light` variants
// stay theme-aware and dark-mode safe.
const datasetPillStyle = (color: string): CSSProperties => ({
    display: 'inline-block',
    fontFamily: FONT,
    fontSize: 'var(--mantine-font-size-xs)',
    fontWeight: 600,
    borderRadius: 'var(--mantine-radius-sm)',
    padding: '2px 8px',
    lineHeight: 1.4,
    color: `var(--mantine-color-${color}-light-color)`,
    background: `var(--mantine-color-${color}-light)`,
})
// CPM price — sits bottom-left, above the action buttons.
const cpmStyle: CSSProperties = {
    fontFamily: FONT,
    fontSize: 'var(--mantine-font-size-sm)',
    fontWeight: 600,
    color: TEXT,
    marginTop: 4,
}
const cpmUnitStyle: CSSProperties = {
    fontSize: 'var(--mantine-font-size-xs)',
    fontWeight: 500,
    color: DIMMED,
}

// Shared mutationKey so the add-to-cart mutation (fired from the tool hook) and
// the per-button status read (useMutationState, below) refer to the same
// mutations in the React Query cache.
export const ADD_TO_CART_MUTATION_KEY = [
    'show-recommended-strategies',
    'add-to-cart',
]

type AddButtonState = 'idle' | 'pending' | 'added' | 'error'

// "Add to cart" CTA. Styled inline (a raw <button>) so it survives the chat
// bubble's CSS reset, like the ChoicePrompt confirm button. The colour set
// follows the request's status: brand while idle/pending, muted once in the
// cart, red on failure (clickable to retry).
const addButtonStyle = (state: AddButtonState): CSSProperties => {
    const base: CSSProperties = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        alignSelf: 'flex-end',
        marginTop: 4,
        padding: '6px 12px',
        borderRadius: 'var(--mantine-radius-sm)',
        border: 'none',
        fontFamily: FONT,
        fontSize: 'var(--mantine-font-size-xs)',
        fontWeight: 600,
        lineHeight: 1.4,
    }
    switch (state) {
        case 'added':
            return {
                ...base,
                cursor: 'default',
                color: DIMMED,
                background: 'var(--mantine-color-default-hover)',
            }
        case 'pending':
            return {
                ...base,
                cursor: 'default',
                color: ON_PRIMARY,
                background: BRAND,
                opacity: 0.75,
            }
        case 'error':
            return {
                ...base,
                cursor: 'pointer',
                color: 'var(--mantine-color-red-light-color)',
                background: 'var(--mantine-color-red-light)',
            }
        default:
            return {
                ...base,
                cursor: 'pointer',
                color: ON_PRIMARY,
                background: BRAND,
            }
    }
}

// "Refine" CTA — a subtle outline button beside "Add to cart". Hands the
// strategy's config to the segment agent (see useShowRecommendedStrategiesTool)
// to edit before adding, rather than adding it as-is.
const refineButtonStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 4,
    padding: '6px 12px',
    borderRadius: 'var(--mantine-radius-sm)',
    border: `1px solid ${BORDER}`,
    background: 'transparent',
    color: TEXT,
    cursor: 'pointer',
    fontFamily: FONT,
    fontSize: 'var(--mantine-font-size-xs)',
    fontWeight: 600,
    lineHeight: 1.4,
}

const str = (v: unknown): string | undefined =>
    typeof v === 'string' && v.trim() ? v : undefined

const isSize = (v: unknown): v is number =>
    typeof v === 'number' && isFinite(v) && v > 0

// Each recommendation IS an AudienceConfiguration — the backend tool validates
// and sizes every one before returning, so `name`/`description`/`audience_size`
// are always present. Read defensively anyway (the items arrive as raw JSON).
type Strategy = AudienceConfiguration

// The reach to show: the configuration's server-computed `audience_size`.
const strategySize = (s: Strategy): number | undefined =>
    isSize(s.audience_size) ? s.audience_size : undefined

// Round n UP to `sig` significant figures, e.g. 15,800,000 -> 16,000,000 and
// 616,000 -> 620,000. Gives the progress bars a clean shared maximum to scale
// against.
const roundUpToSigFigs = (n: number, sig = 2): number => {
    if (!isSize(n)) return 0
    const magnitude = Math.floor(Math.log10(n)) - (sig - 1)
    const factor = 10 ** magnitude
    return Math.ceil(n / factor) * factor
}

// A content key (never the array index) so React is happy and oxlint's
// no-array-index-key stays satisfied.
const strategyKey = (s: Strategy): string =>
    `${str(s.segment_id) ?? ''}:${str(s.name) ?? ''}`

// Shown while the backend `show_recommended_strategies` tool is still running
// (validating + sizing). The cards render only from the completed result, so
// this fills the gap with a compact, on-brand progress note.
export const RecommendedStrategiesPending: FC = () => (
    <div
        style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontFamily: FONT,
            fontSize: 'var(--mantine-font-size-sm)',
            color: DIMMED,
            margin: 'var(--mantine-spacing-xs) 0',
        }}
    >
        <Loader size={16} />
        Finding the best audiences…
    </div>
)

// Display-only chat widget: renders the `Summary` + `Strategies` the agent
// passed as a themed card — a short summary paragraph followed by one bordered
// row per strategy (title, a reach pill for the estimated size, and the data
// description). It does NOT collect input — picking a strategy goes through the
// ChoicePrompt choice widget.
export const RecommendedStrategies: FC<{
    args: Record<string, unknown>
    names: Record<string, string>
    onAddToCart?: (config: AudienceConfiguration) => void
    // Hand a strategy's config to the segment agent to refine before adding.
    onRefine?: (config: AudienceConfiguration) => void
    // The agent whose live state holds the cart — read here (not via a captured
    // prop) so the "Added to cart" state stays current.
    agentId: string
}> = ({ args, names, onAddToCart, onRefine, agentId }) => {
    // Cart membership drives each button's added/idle state. Read it from the
    // LIVE agent state via useAgent rather than a prop: this card is rendered by
    // CopilotKit's useDefaultRenderTool, which keeps the completed tool's element
    // mounted and does NOT re-invoke the render callback when the cart changes —
    // so a captured `cartSegmentIds` prop would go stale after an add. Subscribing
    // here re-renders the card the moment a segment lands in the cart.
    const { agent } = useAgent({ agentId })
    const inCart = new Set(
        (
            (agent.state as AgentState | null | undefined)
                ?.audienceConfigurations ?? []
        ).map((c) => c.segment_id)
    )

    // Per-segment add-to-cart request status, read from the React Query
    // mutation cache (no local state — "added" comes from the cart, the
    // in-flight pending/error states come from here). Later entries win, so a
    // retry's status supersedes the failed attempt's.
    const statusBySegment = new Map<string, string>()
    useMutationState({
        filters: { mutationKey: ADD_TO_CART_MUTATION_KEY },
        select: (m) => ({
            segmentId: (m.state.variables as AudienceConfiguration | undefined)
                ?.segment_id,
            status: m.state.status,
        }),
    }).forEach(({ segmentId, status }) => {
        if (segmentId) statusBySegment.set(segmentId, status)
    })

    const summary = str((args as { summary?: unknown }).summary)
    const raw = (args as { strategies?: unknown }).strategies
    // Drop non-object items (null/undefined/strings) so the map never throws on
    // a malformed strategy.
    const strategies: Strategy[] = Array.isArray(raw)
        ? (raw.filter((s) => s && typeof s === 'object') as Strategy[])
        : []

    if (!summary && !strategies.length) return null

    // A shared maximum (rounded up to 2 significant figures) so every bar is
    // drawn relative to the same scale and the strategies can be compared.
    const sizes = strategies.map(strategySize).filter(isSize)
    const roundedMax = roundUpToSigFigs(sizes.length ? Math.max(...sizes) : 0)

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--mantine-spacing-md)',
                // Match the ask-user widget's width (cardStyle.maxWidth) even
                // though we don't draw its box.
                maxWidth: 600,
                margin: 'var(--mantine-spacing-xs) 0',
            }}
        >
            {summary && <div style={summaryStyle}>{summary}</div>}
            {strategies.length > 0 && (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 'var(--mantine-spacing-sm)',
                    }}
                >
                    {strategies.map((s) => {
                        // Title/subtitle are the configuration's own (required)
                        // name/description — the same fields the cart card shows.
                        // Fall back to a de-underscored segment_id only
                        // defensively; the backend guarantees a name.
                        const title =
                            str(s.name) ||
                            str(s.segment_id)?.replaceAll('_', ' ')
                        const description = str(s.description)
                        const size = strategySize(s)
                        const reach = formatReach(size)
                        // Recommendations are unmodeled, so the CPM is the base
                        // rate for the audience type.
                        const cpm = cpmFor(s.dataset_type, false)
                        // Derive the dataset chip(s) from the configuration's own
                        // dataset ids rather than a separate field. A crossed
                        // config spans two datasets, so show both: `dataset_id`
                        // (transaction side) and `users_with_attributes_dataset_id`
                        // (user-attribute side). Keep each id alongside its label
                        // so the chip's colour stays keyed off the (stable) id.
                        const datasetIds =
                            s.dataset_type === DatasetType.CROSSED
                                ? [
                                      s.dataset_id,
                                      s.users_with_attributes_dataset_id,
                                  ]
                                : [s.dataset_id]
                        const datasets = datasetIds
                            .map((id) => str(id))
                            .filter((id): id is string => Boolean(id))
                            .map((id) => ({
                                id,
                                label: datasetLabel(id, names),
                            }))
                            .filter((d) => d.label)
                        // The configuration is the backend-validated, sized
                        // segment, so it can be added/refined as-is — no
                        // re-validation needed. Offer the actions whenever a
                        // handler is wired.
                        const addable: AudienceConfiguration | null =
                            onAddToCart || onRefine ? s : null
                        // Refine only applies to the segment types the segment
                        // agents edit (transaction / user-attribute); crossed
                        // segments have no editing-state slot.
                        const refinable =
                            onRefine &&
                            addable &&
                            (addable.dataset_type === DatasetType.TRANSACTION ||
                                addable.dataset_type ===
                                    DatasetType.USER_ATTRIBUTE)
                                ? addable
                                : null
                        const isInCart =
                            !!addable && inCart.has(addable.segment_id)
                        const reqStatus = addable
                            ? statusBySegment.get(addable.segment_id)
                            : undefined
                        // Cart membership wins; otherwise reflect the
                        // in-flight request (pending / error) → idle.
                        const buttonState: AddButtonState = isInCart
                            ? 'added'
                            : reqStatus === 'pending'
                              ? 'pending'
                              : reqStatus === 'error'
                                ? 'error'
                                : 'idle'
                        const ratio =
                            isSize(size) && roundedMax > 0
                                ? size / roundedMax
                                : 0
                        // Nothing renderable for this strategy — skip it.
                        if (
                            !title &&
                            !description &&
                            !reach &&
                            !datasets.length
                        )
                            return null
                        return (
                            <div key={strategyKey(s)} style={strategyRowStyle}>
                                {(title || reach) && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                            gap: 8,
                                        }}
                                    >
                                        {title && (
                                            <span
                                                style={{
                                                    ...titleStyle,
                                                    flex: 1,
                                                    minWidth: 0,
                                                }}
                                            >
                                                {title}
                                            </span>
                                        )}
                                        {reach && (
                                            <div
                                                style={{
                                                    flexShrink: 0,
                                                    textAlign: 'right',
                                                }}
                                            >
                                                <div style={reachValueStyle}>
                                                    {reach}
                                                </div>
                                                <div style={reachLabelStyle}>
                                                    Reach
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {description && (
                                    <div style={descStyle}>{description}</div>
                                )}
                                {datasets.length > 0 && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            alignItems: 'center',
                                            gap: 6,
                                            marginTop: 2,
                                        }}
                                    >
                                        {datasets.map((d) => (
                                            <span
                                                key={d.id}
                                                style={datasetPillStyle(
                                                    colorForDataset(d.id)
                                                )}
                                            >
                                                {d.label}
                                            </span>
                                        ))}
                                    </div>
                                )}
                                {ratio > 0 && (
                                    <Progress
                                        value={ratio * 100}
                                        color={BRAND}
                                        size='md'
                                        radius='sm'
                                        mt={4}
                                        aria-label={`Estimated reach, ${formatReach(size)} of ${formatReach(roundedMax)}`}
                                    />
                                )}
                                {cpm !== undefined && (
                                    <div style={cpmStyle}>
                                        {formatCpm(cpm)}{' '}
                                        <span style={cpmUnitStyle}>CPM</span>
                                    </div>
                                )}
                                {(addable || refinable) && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                            gap: 8,
                                        }}
                                    >
                                        {refinable && (
                                            <button
                                                type='button'
                                                style={refineButtonStyle}
                                                onClick={() =>
                                                    onRefine?.(refinable)
                                                }
                                            >
                                                <IconPencil size={14} />
                                                Refine
                                            </button>
                                        )}
                                        {addable && (
                                            <button
                                                type='button'
                                                disabled={
                                                    buttonState === 'added' ||
                                                    buttonState === 'pending'
                                                }
                                                style={addButtonStyle(
                                                    buttonState
                                                )}
                                                onClick={() =>
                                                    onAddToCart?.(addable)
                                                }
                                            >
                                                {buttonState === 'added' && (
                                                    <>
                                                        <IconCheck size={14} />
                                                        Added to cart
                                                    </>
                                                )}
                                                {buttonState === 'pending' && (
                                                    <>
                                                        <Loader
                                                            size={14}
                                                            color={ON_PRIMARY}
                                                        />
                                                        Adding…
                                                    </>
                                                )}
                                                {buttonState === 'error' && (
                                                    <>
                                                        <IconAlertTriangle
                                                            size={14}
                                                        />
                                                        Failed — retry
                                                    </>
                                                )}
                                                {buttonState === 'idle' && (
                                                    <>
                                                        <IconShoppingCartPlus
                                                            size={14}
                                                        />
                                                        Add to cart
                                                    </>
                                                )}
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            )}
        </div>
    )
}
